import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setProgress, setToast } from "../../../../redux/slice/system";
import { RecordAPI } from "../../../../axios/api";
import {
  useAbortedEffect,
  useSearch,
  useTable,
  usePop,
  useConfirm,
} from "../../../../components/hooks";
import useExcel from "../useExcel";
import moment from "moment";

import ModuleBreadCrumb from "../../../../components/breadCrumb";
import ModuleTitle from "../../../../components/common/title";
import ModulePopRecordSugarForm from "./popForm";
import ModulePopRecordChart from "../popChart";
import ModulePopConfirm from "../../../../components/pop/popConfirm";
import { setPageSetting } from "../../../../service/common";
import { system_table, system_dropdown } from "../../../../service/system";
import {
  option_category_obj,
  option_warn_limit,
  option_measure_time_obj,
  option_date_range,
  option_date_range_obj,
} from "../../../../service/option";
import { rateHandler } from "../common";

import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";

export default function MemberRecordSugar({
  moduleType = "member",
  memberData = [],
  closePrePop,
  prePopOption,
  setPreReload,
}) {
  const route_params = useParams();
  const dispatch = useDispatch();
  const redux_user = useSelector((state) => state.user);
  const crumbItem = [
    { key: 1, label: "首頁", href: "/member" },
    { key: 2, label: "記錄", href: "/member/record" },
    { key: 3, label: option_category_obj.record.sugar.label },
  ];
  const [reload, setReload] = useState({ list: false });
  const {
    setTbData,
    tbTotalRecords,
    setTbTotalRecords,
    setTbFirstIndex,
    setTbRows,
    tbColumns,
    setTbColumns,
    renderTable,
    setPageAction,
  } = useTable();
  // 篩選條件
  const defaultFilter = {
      page: 1,
      size: system_table.size,
      dateType: 1,
      startDate: new Date(
        moment()
          .subtract(
            option_date_range_obj[1].diffValue,
            option_date_range_obj[1].diffType
          )
          .format("YYYY-MM-DD")
      ),
      endDate: new Date(),
      memberid: null,
    },
    pagePath = "/record/sugar";
  const {
    filterData,
    setFilterData,
    search,
    setSearch,
    changeFilter,
    renderSearchWrap,
  } = useSearch({
    defaultFilter,
    pagePath: [pagePath],
    tbSetting: { setTbFirstIndex, setTbRows },
    reloadAction: () => setReload((state) => ({ ...state, list: true })),
  });
  const { isPop, popOption, openPop, closePop, renderDialog } = usePop([
    {
      key: "form",
      title: (props) => `${props?.data?.id ? "編輯" : "新增"}`,
      isOpen: false,
    },
    {
      key: "chart",
      title: () => "　",
      isOpen: false,
    },
    {
      key: "confirm",
      title: () => "資料異常",
      isOpen: false,
    },
  ]);
  const { confirmOption, setConfirmOption, renderConfirm } = useConfirm();
  const { exportExcel } = useExcel();

  useEffect(() => {
    const action = ({ startIndex, rows }) => {
      let temp = {
        ...search,
        page: Math.floor(startIndex / rows) + 1,
        size: rows,
      };
      setFilterData(temp);
      setSearch(temp);
    };
    setPageAction(
      () =>
        ({ startIndex, rows }) =>
          action({ startIndex, rows })
    );
  }, [search]);

  // 取得列表資料
  useAbortedEffect(
    (signal) => {
      if (!reload.list) return;

      // setPageSetting({ params: search, page: pagePath });

      getData({ signal, type: "list" });
    },
    [reload]
  );

  const getData = ({ signal, type, page = search.page } = {}) => {
    dispatch(setProgress(true));

    let params = {
      ...search,
      page,
      startDate: search.startDate
        ? moment(search.startDate).format("YYYY-MM-DD")
        : null,
      endDate: search.endDate
        ? moment(search.endDate).format("YYYY-MM-DD")
        : null,
    };
    delete params.dateType;

    let promise;
    if (moduleType === "member" || moduleType === "memberPop") {
      promise = RecordAPI.getMyRecordGlucoseList({
        data: params,
        options: { signal },
      });
    } else {
      params.memberid = route_params.detailid || search.memberid;
      promise = RecordAPI.getRecordGlucoseList({
        data: params,
        options: { signal },
      });
    }

    promise.then((result) => {
      let { Response, data, total, message } = result;

      if (Response === 1) {
        data.sort(function (pre, next) {
          return (
            new Date(next.date) - new Date(pre.date) || next.type - pre.type
          );
        });

        if (type === "chart") {
          const lineKey = [
              {
                key: "glucose",
                label: "血糖",
              },
            ],
            referenceKey = {
              glucose: [
                {
                  value: option_warn_limit.glucose.down,
                },
                {
                  value: option_warn_limit.glucose.up + 0.000001,
                },
              ],
            };

          data = data
            .filter((item) => item.status === 1)
            .sort(function (pre, next) {
              return (
                new Date(pre.date) - new Date(next.date) || pre.type - next.type
              );
            })
            .map((item) => ({
              name: `${item.date} ${option_measure_time_obj[item.type]?.label}`,
              ...lineKey.reduce(
                (pre, cur) => ({
                  ...pre,
                  [cur.label]: item[cur.key],
                }),
                {}
              ),
            }));

          openPop({
            type: "chart",
            data: {
              search,
              data,
              lineKey,
              referenceKey,
            },
          });
        } else if (type === "excel") {
          checkExport({ data });
        } else {
          setTbData(data);
          setTbTotalRecords(total);
          setReload((state) => ({
            ...state,
            list: false,
          }));
        }
      } else {
        dispatch(
          setToast({
            severity: "error",
            summary: message,
            detail: "",
          })
        );
      }

      dispatch(setProgress(false));
    });
  };

  useEffect(() => {
    const renderBtnAction = (data) => (
      <>
        <Button
          className="p-button-sm"
          type="button"
          label="編輯"
          onClick={() => openPop({ type: "form", data })}
        />
        <Button
          id={`button_delete_${data.id}`}
          className="p-button-danger p-button-sm ml-2"
          type="button"
          label="刪除"
          onClick={() =>
            setConfirmOption({
              setting: {
                target: document.getElementById(`button_delete_${data.id}`),
                message: "確認是否刪除?",
                icon: "pi pi-exclamation-triangle",
                visible: !confirmOption.setting.visible,
                acceptLabel: "是",
                rejectLabel: "否",
                accept: () => deleteAction({ data }),
                reject: () => null,
              },
              reOpen: confirmOption.setting.visible,
            })
          }
        />
      </>
    );

    const renderCellStyle = ({ data, field }) => {
      return {
        ...(["glucose"].includes(field) &&
        (data < option_warn_limit[field].down ||
          data > option_warn_limit[field].up)
          ? { font_color: "e35b68" }
          : {}),
      };
    };

    setTbColumns([
      {
        key: "member_name",
        field: "member_name",
        header: "姓名",
        className: "flex-grow-1 w-5rem",
        exportOptions: {
          width: 15,
        },
      },
      {
        key: "date",
        field: "date",
        header: "日期",
        className: "flex-grow-1 w-5rem",
        isDate: true,
        exportOptions: {
          width: 15,
        },
      },
      {
        key: "type",
        field: "type",
        header: "量測時間",
        className: "flex-grow-1 w-5rem",
        body: (data) => (
          <>
            {data.status === 1 ? (
              option_measure_time_obj[data.type]?.label
            ) : (
              <span className="text-red-400">無量測</span>
            )}
          </>
        ),
        exportOptions: {
          width: 15,
          formatVal: ({ allData }) => {
            return allData.status === 1
              ? option_measure_time_obj[allData.type]?.label
              : "無量測";
          },
          bodyColStyles: ({ allData }) => {
            return {
              font_color: allData.status === 1 ? "000000" : "e35b68",
            };
          },
        },
      },
      {
        key: "glucose",
        field: "glucose",
        header: "血糖",
        className: "flex-grow-1 w-5rem",
        body: (data) => <>{data.status === 1 ? data.glucose : "-"}</>,
        decimalShowRaw: true,
        exportOptions: {
          width: 15,
          formatVal: ({ allData }) => {
            return allData.status === 1 ? allData.glucose : "-";
          },
          bodyColStyles: ({ data, field }) => renderCellStyle({ data, field }),
        },
      },
      {
        key: "id",
        field: "id",
        header: "進階",
        className: "w-9rem froze-right",
        alignFrozen: "right",
        frozen: true,
        body: (data) => renderBtnAction(data),
        exportOptions: {
          isSkip: true,
        },
      },
    ]);
  }, [pagePath]);

  const deleteAction = ({ data }) => {
    dispatch(setProgress(true));

    let jsonData = { id: data.id };

    RecordAPI.deleteRecordGlucoseData({ data: jsonData }).then(
      async (result) => {
        let { Response, my_todays_data } = result;

        if (Response === 1) {
          await rateHandler({
            memberid: data.memberid,
            my_todays_data,
            now_date: data.date,
          });

          dispatch(
            setToast({
              severity: "success",
              summary: "成功刪除",
              detail: "",
            })
          );
          setReload((state) => ({
            ...state,
            list: true,
          }));
        } else {
          dispatch(
            setToast({
              severity: "error",
              summary: "刪除失敗",
              detail: "",
            })
          );
          dispatch(setProgress(false));
        }
      }
    );
  };

  // 表格 行 樣式
  const tbCellClass = (value, cellData) => {
    let field = cellData.column.props.field;

    return {
      "text-red-400":
        ["glucose"].includes(field) &&
        (value < option_warn_limit[field].down ||
          value > option_warn_limit[field].up),
    };
  };

  const checkExport = ({ data }) => {
    if (data && data.length > 0) {
      exportExcel({
        fileName: `${option_category_obj.record.sugar.label}`,
        headColumns: tbColumns.filter((item) => !item.exportOptions.isSkip),
        data,
      });
    } else {
      dispatch(
        setToast({
          severity: "warn",
          summary: "無資料",
          detail: "",
        })
      );
    }
  };

  return (
    <>
      {renderConfirm}

      {moduleType === "member" && (
        <>
          <ModuleBreadCrumb crumbItem={crumbItem} />

          <ModuleTitle
            data={{ title: option_category_obj.record.sugar.label }}
          />
        </>
      )}

      <div className="text-red-400 mb-2">
        {`血糖<70、≧180 mg/dL 以紅字顯示`}
      </div>

      {renderSearchWrap({
        isShow: false,
        template: (
          <>
            {moduleType === "back" && (
              <div className="col-12 sm:col-6 md:col-4 xl:col-2 flex align-items-start flex-column mt-2 md:mt-0">
                <label
                  htmlFor="username"
                  className="w-4rem white-space-nowrap text-left mb-1"
                >
                  會員姓名
                </label>
                <Dropdown
                  name="memberid"
                  className="w-full"
                  value={filterData.memberid}
                  onChange={(e) => changeFilter(e)}
                  options={memberData}
                  optionLabel="name"
                  optionValue="id"
                  emptyMessage={system_dropdown.empty}
                  filter={true}
                  filterBy="name"
                  emptyFilterMessage={system_dropdown.emptyFilter}
                  showClear={true}
                />
              </div>
            )}

            <div className="col-12 sm:col-6 md:col-4 xl:col-2 flex align-items-start flex-column mt-2 md:mt-0">
              <label
                htmlFor="dateType"
                className="w-4rem white-space-nowrap text-left mb-1"
              >
                日期範圍類型
              </label>
              <Dropdown
                name="dateType"
                className="w-full"
                value={filterData.dateType}
                onChange={(e) => {
                  if (e.value !== 5) {
                    let dateTypeOpt = option_date_range_obj[e.value],
                      startDate = moment()
                        .subtract(dateTypeOpt.diffValue, dateTypeOpt.diffType)
                        .format("YYYY-MM-DD"),
                      endDate = moment().format("YYYY-MM-DD");
                    setFilterData((state) => ({
                      ...state,
                      dateType: e.value,
                      startDate: new Date(startDate),
                      endDate: new Date(endDate),
                    }));
                  } else changeFilter(e);
                }}
                options={option_date_range}
                optionLabel="label"
                optionValue="value"
                emptyMessage={system_dropdown.empty}
                showClear={false}
              />
            </div>

            <div
              className={classNames(
                "col-12 sm:col-6 md:col-4 xl:col-2 flex align-items-start flex-column mt-2 md:mt-0"
              )}
            >
              <label
                htmlFor="username"
                className="w-4rem white-space-nowrap text-left mb-1"
              >
                起始日期
              </label>
              <Calendar
                className="w-full"
                name="startDate"
                value={filterData.startDate}
                onChange={(e) => changeFilter(e)}
                readOnlyInput
                showButtonBar
                disabled={filterData.dateType !== 5}
              />
            </div>
            <div
              className={classNames(
                "col-12 sm:col-6 md:col-4 xl:col-2 flex align-items-start flex-column mt-2 md:mt-0"
              )}
            >
              <label
                htmlFor="username"
                className="w-4rem white-space-nowrap text-left mb-1"
              >
                結束日期
              </label>
              <Calendar
                className="w-full"
                name="endDate"
                value={filterData.endDate}
                onChange={(e) => changeFilter(e)}
                readOnlyInput
                showButtonBar
                disabled={filterData.dateType !== 5}
              />
            </div>
            <div
              className={classNames("flex align-items-end", {
                "col-12 md:col-8 xl:col-4 md:mt-0 mt-2": moduleType === "back",
                "col-12 sm:col-6 md:col-12 xl:col-6 xl:mt-0 md:mt-2 sm:mt-0 mt-2":
                  moduleType !== "back",
              })}
            >
              <Button
                className={classNames(
                  "p-button-info p-button-sm w-auto px-3 mr-2",
                  {
                    "md:ml-0 ml-auto": moduleType === "back",
                    "ml-auto sm:ml-0 md:ml-auto xl:ml-0": moduleType !== "back",
                  }
                )}
                type="button"
                icon="pi pi-search"
                label="查詢"
                onClick={() => {
                  setSearch({
                    ...filterData,
                    page: 1,
                  });
                  setTbFirstIndex(0);
                }}
              />

              <Button
                className={classNames("p-button-help p-button-sm w-auto px-3", {
                  "md:ml-auto ml-0": moduleType === "back",
                  "ml-0 sm:ml-auto md:ml-0 xl:ml-auto": moduleType !== "back",
                })}
                type="button"
                label="折線圖"
                onClick={() => getData({ type: "chart", page: -1 })}
              />
              {/* {moduleType === "back" && ( */}
              <Button
                className="p-button-secondary p-button-sm w-auto ml-2 px-3"
                type="button"
                label="匯出"
                onClick={() => getData({ type: "excel", page: -1 })}
              />
              {/* )} */}
              <Button
                className="p-button-warning p-button-sm w-auto ml-2 px-3"
                type="button"
                label="新增"
                onClick={() => openPop({ type: "form", data: null })}
              />
            </div>
          </>
        ),
      })}

      {renderTable({
        className: "mt-2",
        options: {
          scrollable: true,
          scrollDirection: "both",
          cellClassName: tbCellClass,
        },
      })}

      {moduleType === "memberPop" && (
        <div className="text-right">
          <Button
            type="button"
            label="關閉"
            className="p-button-secondary ml-auto px-3"
            onClick={() => {
              closePrePop({ type: prePopOption.type });
              setPreReload((state) => ({ ...state, list: true }));
            }}
          />
        </div>
      )}

      {renderDialog({
        className: classNames("w-11 md:w-10 lg:w-8", {
          "max-w-30rem": isPop.form?.isOpen || isPop.confirm?.isOpen,
        }),
        children: (
          <>
            {isPop.form?.isOpen && (
              <ModulePopRecordSugarForm
                prePopOption={popOption}
                closePrePop={closePop}
                openPrePop={openPop}
                setPreReload={setReload}
                memberid={
                  route_params.detailid
                    ? Number(route_params.detailid)
                    : redux_user.userDetail.id
                }
                moduleType={moduleType}
                memberData={memberData}
              />
            )}
            {isPop.chart?.isOpen && (
              <ModulePopRecordChart
                prePopOption={popOption}
                closePrePop={closePop}
              />
            )}
            {isPop.confirm?.isOpen && (
              <ModulePopConfirm
                prePopOption={popOption}
                closePrePop={closePop}
              />
            )}
          </>
        ),
      })}
    </>
  );
}
